import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchModulesList,
  DataReceiverError,
} from "../../../../utils/dataReceiver";
import useTableSort from "../../../hooks/useTableSort";
import SortableTableHeader from "../SortableTableHeader";

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Parse an actualOnlineDate string into a sortable timestamp.
 * Falls back to 0 if missing / unparseable.
 */
function toTs(dateStr) {
  if (!dateStr) return 0;
  return new Date(dateStr).getTime() || 0;
}

/**
 * Format an actualOnlineDate string for display (date portion only).
 * Full ISO string is preserved in the cell's title attribute.
 */
function formatDate(dateStr) {
  if (!dateStr) return "-";
  return dateStr.split("T")[0].split(" ")[0];
}

const COLUMN_COUNT = 7;

const OnlineMods = () => {
  const navigate = useNavigate();

  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize table sorting with default sort by actualOnlineDate descending
  const { sortedData, requestSort, getSortClass } = useTableSort(modules, {
    initialKey: "actualOnlineDate",
    initialDirection: "desc",
  });

  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    (async () => {
      try {
        setLoading(true);
        setError(null);

        const rows = await fetchModulesList();
        setModules(rows);
      } catch (err) {
        setError(
          err instanceof DataReceiverError
            ? err.message
            : "Unexpected error loading modules list.",
        );
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleRowClick = (workOrder) => {
    navigate(`/module-metrics?workOrder=${encodeURIComponent(workOrder)}`);
  };

  return (
    <>
      <p className="modal-description">
        Units currently on physical line stations
      </p>

      <div className="modal-section modal-section--scroll">
        <h3>Active Line Items</h3>

        <div className="modal-table-scroll allow-x-scroll">
          <table className="modal-table modal-table--clickable">
            <thead>
              <tr>
                <SortableTableHeader
                  columnKey="Property"
                  onSort={requestSort}
                  sortClass={getSortClass("Property")}
                >
                  Property
                </SortableTableHeader>
                <SortableTableHeader
                  columnKey="scheduledOnlineDate"
                  onSort={requestSort}
                  sortClass={getSortClass("scheduledOnlineDate")}
                >
                  Scheduled Online
                </SortableTableHeader>
                <SortableTableHeader
                  columnKey="actualOnlineDate"
                  onSort={requestSort}
                  sortClass={getSortClass("actualOnlineDate")}
                >
                  Actual Online
                </SortableTableHeader>
                <SortableTableHeader
                  columnKey="WoNum"
                  onSort={requestSort}
                  sortClass={getSortClass("WoNum")}
                >
                  Work Order
                </SortableTableHeader>
                <SortableTableHeader
                  columnKey="ItemNum"
                  onSort={requestSort}
                  sortClass={getSortClass("ItemNum")}
                >
                  Item Number
                </SortableTableHeader>
                <SortableTableHeader
                  columnKey="BatchNum"
                  onSort={requestSort}
                  sortClass={getSortClass("BatchNum")}
                >
                  Batch Number
                </SortableTableHeader>
                <SortableTableHeader
                  columnKey="Sqft"
                  onSort={requestSort}
                  sortClass={getSortClass("Sqft")}
                >
                  SQFT
                </SortableTableHeader>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td
                    colSpan={COLUMN_COUNT}
                    className="modal-table-cell--center"
                  >
                    Loading modules…
                  </td>
                </tr>
              )}

              {!loading && error && (
                <tr>
                  <td
                    colSpan={COLUMN_COUNT}
                    className="modal-table-cell--center modal-table-cell--error"
                  >
                    {error}
                  </td>
                </tr>
              )}

              {!loading && !error && sortedData.length === 0 && (
                <tr>
                  <td
                    colSpan={COLUMN_COUNT}
                    className="modal-table-cell--center"
                  >
                    No active modules found.
                  </td>
                </tr>
              )}

              {!loading &&
                !error &&
                sortedData.map((mod) => (
                  <tr
                    key={mod.WoNum}
                    onClick={() => handleRowClick(mod.WoNum)}
                    className="modal-table-row--clickable"
                    title="Click to view module details"
                  >
                    <td>{mod.Property}</td>
                    <td>{mod.scheduledOnlineDate ?? "-"}</td>
                    <td title={mod.actualOnlineDate ?? undefined}>
                      {formatDate(mod.actualOnlineDate)}
                    </td>
                    <td>{mod.WoNum}</td>
                    <td>{mod.ItemNum}</td>
                    <td>{mod.BatchNum}</td>
                    <td>
                      {mod.Sqft != null
                        ? Number(mod.Sqft).toLocaleString()
                        : "-"}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default OnlineMods;
